import { useTranslation } from "react-i18next";
import ItemRow, { SelectableItem } from "./item-row";
import { useEffect, useRef, useState } from "react";
import ScSearchInput from "../../ui-kit/search-bar";
import { useTheme } from "@/src/styles/theme-provider";
import { SearchNormal1 } from "iconsax-react-nativejs";
import {
  ActivityIndicator,
  FlatList,
  LayoutChangeEvent,
  Pressable,
  Text,
  View,
} from "react-native";

type ItemSelectorListProps = Readonly<{
  items: SelectableItem[];
  selectedIds: string[];
  searchPlaceholderKey?: string;
  filterFn?: (item: SelectableItem, search: string) => boolean;
  onToggle: (item: SelectableItem) => void;
  onClear: () => void;
  onEndReached?: () => void;
  isFetchingMore?: boolean;
  /** Shown when the caller has nothing to list at all. */
  emptyLabelKey?: string;
  /** Pixel height the caller has for this whole component (sheet minus its
   *  header/footer) — split below between the search/count row and the
   *  FlatList. Undefined until that first layout pass lands, so the list
   *  falls back to flex-based sizing for that one frame. */
  availableHeight?: number;
  /**
   * Server-side search. When set, the typed text is sent here (debounced)
   * instead of filtering `items` locally — `items` is then expected to already
   * be the matches. Needed for paged sources: a local filter only ever sees the
   * pages loaded so far, so anyone past them could never be found.
   */
  onSearchChange?: (search: string) => void;
  /** Total matches on the server, for the count label. Defaults to `items`. */
  totalCount?: number;
}>;

const SEARCH_DEBOUNCE_MS = 300;

const ItemSelectorList = ({
  items,
  selectedIds,
  searchPlaceholderKey = "LABEL_SEARCH",
  filterFn,
  onToggle,
  onClear,
  onEndReached,
  isFetchingMore,
  emptyLabelKey = "LABEL_NOTHING_TO_SELECT",
  availableHeight,
  onSearchChange,
  totalCount,
}: ItemSelectorListProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [search, setSearch] = useState("");
  const [topHeight, setTopHeight] = useState(0);

  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    if (!onSearchChangeRef.current) return;
    const id = setTimeout(
      () => onSearchChangeRef.current?.(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [search]);

  // The search box state lives here, so a reopened picker starts empty — the
  // caller's server-side term must reset with it or the list stays narrowed.
  useEffect(() => () => onSearchChangeRef.current?.(""), []);

  const filtered = onSearchChange
    ? items
    : items.filter((item) => filterFn!(item, search));

  // A definite pixel height (availableHeight minus the search/count row) is
  // what actually keeps the FlatList from running on underneath the
  // confirm/cancel footer — see the note on ItemSelectorModal. The
  // flexGrow/minHeight fallback only covers the frame before both
  // measurements land.
  const flatListHeight =
    availableHeight != null && topHeight > 0
      ? Math.max(availableHeight - topHeight, 100)
      : undefined;

  return (
    <>
      {/* Search + count, measured together so the FlatList below knows
          exactly how much height they take. */}
      <View
        onLayout={(e: LayoutChangeEvent) =>
          setTopHeight(e.nativeEvent.layout.height)
        }
      >
        <View className="px-5 pt-4 pb-2">
          <ScSearchInput
            value={search}
            placeholderKey={searchPlaceholderKey}
            containerClassName="rounded-full"
            onChangeText={setSearch}
          />
        </View>

        <View className="flex-row justify-between items-center px-5 py-2">
          <Text className="text-sm text-slate">
            {selectedIds.length > 0
              ? t("LABEL_SELECTED_COUNT", { count: selectedIds.length })
              : t("LABEL_ITEMS_COUNT", {
                  count: totalCount ?? filtered.length,
                })}
          </Text>
          <Pressable onPress={onClear} className="hover:bg-ash-3 hover:rounded-lg">
            <Text className="text-slate hover:underline">
              {t("BTN_CLEAR")}
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Item list */}
      <FlatList
        data={filtered}
        keyExtractor={(item) => item.itemId}
        // No explicit `overflow` here — ScrollView/FlatList's `style` prop
        // lands on the same node that already carries its internal
        // `overflow-y: auto`, which is what makes it clip AND scroll at once.
        // Setting `overflow: "hidden"` there overrides that and disables
        // scrolling entirely, so the definite height alone is the fix; RN's
        // own scroll clipping does the rest.
        style={
          flatListHeight != null
            ? { height: flatListHeight }
            : { flexGrow: 1, flexShrink: 1, minHeight: 0 }
        }
        renderItem={({ item }) => (
          <View className="px-5">
            <ItemRow
              item={item}
              selected={selectedIds.includes(item.itemId)}
              onToggle={() => onToggle(item)}
            />
          </View>
        )}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={() => (
          <View className="mx-5 border-b border-ash-3" />
        )}
        // Breathing room so the final row scrolls clear of the footer divider
        // instead of stopping flush against it.
        contentContainerStyle={{ paddingBottom: 12 }}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.3}
        // Two ways to end up with nothing, and they read differently.
        // A search that matched nothing names the search.
        // An empty source needs the caller's own wording.
        // Without this the list was simply blank.
        ListEmptyComponent={
          <View className="items-center justify-center gap-2 px-6 py-10">
            <SearchNormal1
              size={32}
              color={themeVars["--color-slate-default"] as string}
            />
            <Text className="text-sm text-slate text-center">
              {search.trim()
                ? `${t("LABEL_NO_RESULTS_FOR")}${search.trim()}`
                : t(emptyLabelKey)}
            </Text>
          </View>
        }
        ListFooterComponent={
          isFetchingMore ? <ActivityIndicator className="py-4" /> : null
        }
      />
    </>
  );
};

export default ItemSelectorList;
